/**
 * Typed schema for the `agents.config` / `agent_versions.config_json` JSON blob.
 *
 * Two postures, deliberately:
 *  - `agentConfigWriteSchema` is STRICT — the single validated write path
 *    (version publish) rejects unknown keys so a typo'd capability
 *    (`webSearchEnabld`) is a 400 at author time instead of a
 *    silently-disabled no-op at runtime.
 *  - `agentConfigReadSchema` is LENIENT (passthrough) — historical rows may carry
 *    keys this schema doesn't model yet, and the runtime must never fail to load
 *    an agent because of config drift.
 *
 * Lives in `@quovy/types` (not `@quovy/agents`) because the web builder and the
 * agent runtime both depend on the shape and the web app cannot import the
 * runtime package.
 */
import { z } from "zod";

import { isPublicIdOfType } from "./public-id";
import { stagedFoldersSchema, validateStagedFolders } from "./staged-folders";

/**
 * Upper bound on `config.maxSteps`. Generous against the 50-step default so the
 * override stays useful, but bounded — see the field's doc comment for why an
 * unbounded value is a cost problem rather than a config problem.
 */
export const AGENT_MAX_STEPS_CEILING = 200;
/** The name of the platform-provided agent every workspace is given; no other non-personal agent in a workspace may hold it. */
export const DEFAULT_SYSTEM_AGENT_NAME = "Quovy Agent";

/**
 * Workspace role a personal agent's computer credential carries. Bounded at
 * the owner's own role when saved (PATCH /internal/dashboard/personal-agent).
 */
export const agentComputerRoleSchema = z.enum(["member", "admin"]);
export type AgentComputerRole = z.infer<typeof agentComputerRoleSchema>;

// Persistent computer settings for a personal agent (experimental, docs/roadmap/persistent-agent-computers.md).
export const agentComputerActionSchema = z.enum(["shutdown", "reset"]);
export type AgentComputerAction = z.infer<typeof agentComputerActionSchema>;

export const agentComputerConfigSchema = z.object({
  enabled: z.boolean(),
  role: agentComputerRoleSchema,
  /** Provider work the worker still owes for this computer; cleared when the job lands. */
  pendingAction: agentComputerActionSchema.nullable().optional(),
  /** Why the last shutdown/reset did not complete; null after a successful one. */
  lastActionError: z.string().nullable().optional(),
});
export type AgentComputerConfig = z.infer<typeof agentComputerConfigSchema>;

/**
 * The `computer` tool's screen. `"direct"` is the sandbox's own Linux desktop;
 * `{ target }` is a Windows desk of that remote desktop target, reached through
 * the customer's bridge. A target id is workspace-local.
 */
export const AgentComputerUseSchema = z.union([
  z.literal("direct"),
  z
    .object({
      // Annotated `boolean` so the guard's brand does not reach every reader of the config.
      target: z.string().refine((id): boolean => isPublicIdOfType(id, "remote_desktop_target"), {
        message:
          "computerUse.target must be a remote desktop target id (rdt_…). Copy it from Settings → Remote desktops.",
      }),
    })
    .strict(),
]);
export type AgentComputerUse = z.infer<typeof AgentComputerUseSchema>;

export type ComputerUseMode =
  | { kind: "direct" }
  | { kind: "remote_desktop"; targetPublicId: string };

/** What an agent's `config.computerUse` asks for; null when computer use is off or unreadable. */
export function computerUseMode(
  config: { computerUse?: unknown } | null | undefined,
): ComputerUseMode | null {
  const parsed = AgentComputerUseSchema.safeParse(config?.computerUse);
  if (!parsed.success) return null;
  return parsed.data === "direct"
    ? { kind: "direct" }
    : { kind: "remote_desktop", targetPublicId: parsed.data.target };
}

/** Per-category tool gates. Default-on; set a key `false` to hide that category. */
export const agentToolPolicySchema = z.object({
  sandbox: z.boolean().optional(),
  uploads: z.boolean().optional(),
  tasks: z.boolean().optional(),
  workflows: z.boolean().optional(),
  integrations: z.boolean().optional(),
  /** Workspace email tools kill switch (default on); mailbox grants are the opt-in. */
  email: z.boolean().optional(),
  /** Tag writes, so a read grant never confers mutation on its own. Opt in with `true`. */
  tags: z.boolean().optional(),
  /** create_listener / list_listeners / cancel_listener kill switch (default on). */
  listeners: z.boolean().optional(),
});

/** The known, modeled fields of an agent's `config` JSON. */
const agentConfigShape = {
  /** Allowlist of workspace skill public ids wired into load_skill(). */
  skillIds: z.array(z.string()).optional(),
  /**
   * Vault credential public ids (`vlc_…`) a published version may exchange in
   * computer use. The only credential grant: a `<credential>` chip is guidance.
   */
  credentialIds: z.array(z.string()).optional(),
  /** Attach the model provider's native web-search tool (model must support it). */
  webSearchEnabled: z.boolean().optional(),
  /**
   * Custom_model_* / custom_record_* tools. User agents opt in with `true`;
   * the workspace system agent (Quovy Agent) is on unless this is explicitly
   * `false`.
   */
  customModelsEnabled: z.boolean().optional(),
  /** Per-category tool gates. */
  toolPolicy: agentToolPolicySchema.optional(),
  /**
   * Override the agent-loop step budget. Hard-capped: this is the ONLY bound on
   * a background run's LLM call count — the worker path has no wall-clock
   * timeout and no token budget, and execute.ts passes this straight to
   * `stopWhen`. Uncapped, one config field buys unbounded inference spend and
   * an indefinitely pinned worker slot.
   */
  maxSteps: z.number().int().min(1).max(AGENT_MAX_STEPS_CEILING).optional(),
  /** The agent has the `computer` tool and drives this screen itself; see AgentComputerUseSchema. */
  computerUse: AgentComputerUseSchema.optional(),
  /**
   * SharePoint folders the platform maps into the run sandbox before the
   * agent loop starts (listing + small-folder prefetch + fetch_staged_file).
   * Wiring only; see ./staged-folders.ts.
   */
  staged_folders: stagedFoldersSchema.optional(),
  /** Personal agent's persistent computer (see agentComputerConfigSchema). */
  computer: agentComputerConfigSchema.optional(),
} as const;

/**
 * The retired declaration keys. Rejected on write with a message naming the
 * replacement; tolerated on read so historical versions still parse
 * (`stagedFoldersFromConfig` honours their folder sources). DELETE both the
 * read tolerance and this rejection in Milestone 4 of
 * docs/exec-plans/active/staged-folders-retire-sources-destinations.md.
 */
const RETIRED_CONFIG_KEYS = {
  sources:
    'sources is no longer supported. Move folder sources to "staged_folders" (same name and "from" binding); single-file and trigger sources are staged by the agent\'s normal tools and need no declaration.',
  destinations:
    "destinations is no longer supported. Files the agent writes are collected with attach_sandbox_file and delivered with its normal provider tools; remove this key.",
} as const;

/** STRICT — use on the write path (version publish) to reject typos/unknown keys. */
export const agentConfigWriteSchema = z
  .object({
    ...agentConfigShape,
    sources: z.never({ error: RETIRED_CONFIG_KEYS.sources }).optional(),
    destinations: z.never({ error: RETIRED_CONFIG_KEYS.destinations }).optional(),
  })
  .strict()
  .superRefine((config, ctx) => {
    // Publish-time workspace validation (is the bound site registered on a
    // granted integration?) lives on the publish route, not here.
    for (const problem of validateStagedFolders(config.staged_folders ?? [])) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: problem.message,
        path: [problem.field],
      });
    }
  });

/** LENIENT — use to type/parse stored config; tolerates unmodeled keys. */
export const agentConfigReadSchema = z.object(agentConfigShape).passthrough();

const WRITABLE_CONFIG_KEYS: ReadonlySet<string> = new Set(Object.keys(agentConfigShape));

/**
 * The stored config minus every key the write schema no longer models. A
 * published version is immutable, so one minted before a key was retired
 * carries it forever, and republishing it verbatim would be rejected.
 */
export function withoutRetiredAgentConfigKeys(
  config: Record<string, unknown>,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(config).filter(([key]) => WRITABLE_CONFIG_KEYS.has(key)),
  );
}

/** The typed shape of an agent's `config`. Assignable to `Record<string, unknown>`. */
export type AgentConfigData = z.infer<typeof agentConfigReadSchema>;

export type AgentToolPolicy = z.infer<typeof agentToolPolicySchema>;

/**
 * Retype a raw stored config blob as {@link AgentConfigData} without altering it.
 * Validation is enforced on the write path; at read time we only need the type,
 * and must never drop a historical row's data, so this is a pure cast (with the
 * `?? {}` empty-default the loaders already applied).
 */
export function asAgentConfig(raw: unknown): AgentConfigData {
  return (raw ?? {}) as AgentConfigData;
}

/**
 * The Vault credentials a config grants, deduped in listed order. Stored config
 * is cast rather than parsed on read, so a malformed value grants nothing.
 */
export function agentCredentialIds(config: unknown): string[] {
  const ids =
    typeof config === "object" && config !== null
      ? (config as { credentialIds?: unknown }).credentialIds
      : undefined;
  if (!Array.isArray(ids)) return [];
  return [...new Set(ids.filter((id): id is string => typeof id === "string" && id.length > 0))];
}

/**
 * Per-agent half of the custom-models gate.
 *
 * User and code-defined agents opt in with `customModelsEnabled: true`. The
 * workspace system agent (Quovy Agent) is the general-purpose assistant, so it
 * is on unless a super-admin sets `customModelsEnabled: false`.
 */
export function agentAllowsCustomModels(agent: {
  kind?: string | null | undefined;
  config?: { customModelsEnabled?: unknown } | null | undefined;
}): boolean {
  const enabled = agent.config?.customModelsEnabled;
  if (agent.kind === "system") return enabled !== false;
  return enabled === true;
}
